declare global {
    interface Window {
        assetNetworkManager: AssetNetworkManager | null;
    }
}

/**
 * Retrieves the global asset network manager.
 * To use the asset network manager, simply use window.assetNetworkManager
 */
export function getGlobalAssetNetworkManager(): AssetNetworkManager {
    if (
        !window.assetNetworkManager ||
        window.assetNetworkManager.classType !== 'AssetNetworkManager'
    ) {
        window.assetNetworkManager = new AssetNetworkManager();
    }
    return window.assetNetworkManager;
}

/**
 * Removes the global asset network manager.
 * Primarily for use between tests to ensure that the global asset network manager doesn't pollute tests
 */
export function clearGlobalAssetNetworkManager() {
    if (window.assetNetworkManager) {
        window.assetNetworkManager = null;
    }
}

export type LoadableAsset = {
    isLoaded: boolean;
    failedToLoad: boolean;
    parent: null | unknown;
    destroyed?: boolean;
    // this field is only applicable for AudioAsset (used for null check)
    source?: unknown;
};

/**
 * The AssetNetworkManager is a utility that should be loaded globally and allows network assets to register for a
 * network slot.  The AssetNetworkManager will call the provided callback when it's optimal for the asset to load.
 *
 * We don't want everything to try to load all at the same time because:
 *  - browsers have per-domain maximum simultaneous requests and per-tab max requests
 *  - too many requests will mean more than expected will timeout
 *  - too many requests will slow down the user experience
 */
export class AssetNetworkManager {
    classType = 'AssetNetworkManager';

    MAX_SIMULTANEOUS_REQUESTS: number;
    /**
     * A list of network request callbacks that have been received and not yet triggered
     */
    #requests: { asset: LoadableAsset; callback: Function }[] = [];
    /**
     * A list of assets which are currently loading
     */
    #loadingAssets: LoadableAsset[] = [];

    #interval: number | null = null;

    constructor(max_simultaneous_requests = 20) {
        this.MAX_SIMULTANEOUS_REQUESTS = max_simultaneous_requests;
    }

    _boundCheckLoadingAssets = () => {
        this.#checkLoadingAssets();
    };

    destroy() {
        if (this.#interval) {
            window.clearInterval(this.#interval);
            this.#interval = null;
        }
    }

    /**
     * @param asset - the asset that's loading, should have isLoaded, failedToLoad, and container properties
     * @param callback - the method to call when a network slot is available
     */
    waitForNetworkSlot(asset: LoadableAsset, callback: (requestFinished: () => void) => void) {
        this.#requests.push({
            asset,
            callback,
        });
        this.#checkLoadingAssets();
    }

    #freeNetworkSlot(asset: LoadableAsset) {
        const indexOfAsset = this.#loadingAssets.indexOf(asset);
        if (indexOfAsset !== -1) {
            this.#loadingAssets.splice(indexOfAsset, 1);
            this.#checkLoadingAssets();
        }
    }

    #checkLoadingAssets() {
        // remove any loaded/failed assets
        const loaded_assets = this.#loadingAssets.filter(
            // check if the asset has loaded, failed to load, is no longer attached to a container, or has been cleared
            (asset) =>
                asset.isLoaded ||
                asset.failedToLoad ||
                asset.parent === null ||
                asset.source === null,
        );
        if (loaded_assets) {
            // remove assets from loaded_assets
            loaded_assets.forEach((asset) => {
                this.#loadingAssets.splice(this.#loadingAssets.indexOf(asset), 1);
            });
        }

        // add new requests for any open slots
        while (
            this.#loadingAssets.length < this.MAX_SIMULTANEOUS_REQUESTS &&
            this.#requests.length > 0
        ) {
            const request = this.#requests.shift(); // grab first request
            if (!request || !request.asset || request.asset.destroyed) {
                // asset destroyed, continue!
                continue;
            }
            this.#loadingAssets.push(request.asset);
            request.callback(() => {
                this.#freeNetworkSlot(request.asset);
            });
        }

        // register periodic checks if any loading assets
        if (this.#loadingAssets.length > 0 && this.#interval === null) {
            this.#interval = window.setInterval(this._boundCheckLoadingAssets, 100);
        }
        // conversely, unregister checks if nothing to check!
        else if (this.#loadingAssets.length === 0 && this.#interval !== null) {
            window.clearInterval(this.#interval);
            this.#interval = null;
        }
    }
}
